import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import adminAPI from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { toast } from 'sonner';
import { ArrowLeft, Ban, CheckCircle, Trash2, Mail, Phone, Calendar, Target, Activity } from 'lucide-react';

export default function UserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { admin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadUser();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUser = async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await adminAPI.getUserDetail(userId);
      setData(d);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    try { await adminAPI.disableUser(userId); toast.success('Bruker deaktivert'); loadUser(); } catch (err) { toast.error(err.message); }
  };
  const handleEnable = async () => {
    try { await adminAPI.enableUser(userId); toast.success('Bruker aktivert'); loadUser(); } catch (err) { toast.error(err.message); }
  };
  const handleDelete = async () => {
    if (!window.confirm('Slett bruker permanent?')) return;
    try { await adminAPI.deleteUser(userId); toast.success('Bruker slettet'); navigate('/admin/users'); } catch (err) { toast.error(err.message); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent rounded-full animate-spin" /></div>;
  if (error) return <div className="text-center py-20"><p className="text-[#FF3B30] mb-4">{error}</p><button onClick={loadUser} className="text-sm text-[#CCFF00]">Prøv igjen</button></div>;

  const user = data?.user || data || {};
  const profile = data?.profile || {};
  const plan = data?.training_plan || {};
  const activities = data?.recent_activities || [];
  const chatCount = data?.chat_message_count || 0;

  return (
    <div>
      <button onClick={() => navigate('/admin/users')} className="flex items-center gap-2 text-sm text-white/40 hover:text-white/70 mb-6 transition-colors duration-180">
        <ArrowLeft className="w-4 h-4" /> Tilbake til brukere
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Main info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Header card */}
          <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-6">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h1 className="font-['Barlow_Condensed'] font-bold text-2xl sm:text-3xl text-white">{user.name || 'Ukjent bruker'}</h1>
                <div className="flex flex-wrap items-center gap-4 mt-2">
                  {user.email && <span className="flex items-center gap-1.5 text-sm text-white/50"><Mail className="w-3.5 h-3.5" />{user.email}</span>}
                  {user.phone && <span className="flex items-center gap-1.5 text-sm text-white/50"><Phone className="w-3.5 h-3.5" />{user.phone}</span>}
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-medium ${
                user.status === 'disabled' ? 'bg-[#FF3B30]/15 text-[#FF3B30]' : 'bg-[#34C759]/15 text-[#34C759]'
              }`}>
                {user.status === 'disabled' ? 'Deaktivert' : 'Aktiv'}
              </span>
            </div>
            <div className="flex flex-wrap gap-3">
              {user.status === 'disabled' ? (
                <button onClick={handleEnable} className="h-10 px-4 rounded-xl bg-[#34C759]/10 text-[#34C759] text-sm font-medium flex items-center gap-2 hover:bg-[#34C759]/20 transition-[background-color] duration-180">
                  <CheckCircle className="w-4 h-4" /> Aktiver
                </button>
              ) : (
                <button onClick={handleDisable} className="h-10 px-4 rounded-xl bg-[#FF9F0A]/10 text-[#FF9F0A] text-sm font-medium flex items-center gap-2 hover:bg-[#FF9F0A]/20 transition-[background-color] duration-180">
                  <Ban className="w-4 h-4" /> Deaktiver
                </button>
              )}
              {admin?.role === 'superadmin' && (
                <button onClick={handleDelete} className="h-10 px-4 rounded-xl bg-[#FF3B30]/10 text-[#FF3B30] text-sm font-medium flex items-center gap-2 hover:bg-[#FF3B30]/20 transition-[background-color] duration-180">
                  <Trash2 className="w-4 h-4" /> Slett
                </button>
              )}
            </div>
          </div>

          {/* Profile */}
          {profile && Object.keys(profile).length > 0 && (
            <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-6">
              <p className="uppercase tracking-[0.18em] text-[11px] font-medium text-white/40 mb-4">PROFIL</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                {profile.goal_type && (
                  <div>
                    <p className="text-[10px] text-white/25 uppercase tracking-wider mb-1">Mål</p>
                    <p className="text-sm text-white capitalize">{profile.goal_type?.replace(/_/g, ' ')}</p>
                  </div>
                )}
                {profile.experience_level && (
                  <div>
                    <p className="text-[10px] text-white/25 uppercase tracking-wider mb-1">Nivå</p>
                    <p className="text-sm text-white capitalize">{profile.experience_level}</p>
                  </div>
                )}
                {profile.target_date && (
                  <div>
                    <p className="text-[10px] text-white/25 uppercase tracking-wider mb-1">Måldato</p>
                    <p className="text-sm text-white">{new Date(profile.target_date).toLocaleDateString('nb-NO')}</p>
                  </div>
                )}
                {profile.event_name && (
                  <div>
                    <p className="text-[10px] text-white/25 uppercase tracking-wider mb-1">Event</p>
                    <p className="text-sm text-white">{profile.event_name}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Activities */}
          {activities.length > 0 && (
            <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-6">
              <p className="uppercase tracking-[0.18em] text-[11px] font-medium text-white/40 mb-4">SISTE AKTIVITETER</p>
              <div className="space-y-2">
                {activities.map((a, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-white/[0.03] last:border-0">
                    <div className="flex items-center gap-3">
                      <Activity className="w-4 h-4 text-[#5AC8FA]" />
                      <div>
                        <p className="text-sm text-white capitalize">{a.type || 'Løp'}</p>
                        <p className="text-[10px] text-white/30">{a.date ? new Date(a.date).toLocaleDateString('nb-NO') : ''}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {a.distance && <p className="text-sm text-white">{a.distance} km</p>}
                      {a.duration && <p className="text-[10px] text-white/30">{a.duration} min</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Stats */}
          <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-5">
            <p className="uppercase tracking-[0.18em] text-[11px] font-medium text-white/40 mb-4">STATISTIKK</p>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-white/50">Chat-meldinger</span>
                <span className="font-['Barlow_Condensed'] font-bold text-lg text-white">{chatCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-white/50">Aktiviteter</span>
                <span className="font-['Barlow_Condensed'] font-bold text-lg text-white">{activities.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-white/50">Onboardet</span>
                <span className={`text-sm font-medium ${user.onboarding_complete ? 'text-[#34C759]' : 'text-white/30'}`}>
                  {user.onboarding_complete ? 'Ja' : 'Nei'}
                </span>
              </div>
            </div>
          </div>

          {/* Training Plan */}
          {plan && Object.keys(plan).length > 0 && plan.plan && (
            <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-5">
              <p className="uppercase tracking-[0.18em] text-[11px] font-medium text-white/40 mb-4">TRENINGSPLAN</p>
              <div className="space-y-2">
                {plan.current_phase && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white/50">Fase</span>
                    <span className="text-sm text-[#CCFF00] capitalize">{plan.current_phase}</span>
                  </div>
                )}
                {plan.current_week !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white/50">Uke</span>
                    <span className="text-sm text-white">{plan.current_week}</span>
                  </div>
                )}
                {Array.isArray(plan.plan) && plan.plan.map((block, i) => (
                  <div key={i} className="mt-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.03]">
                    <p className="text-xs text-white/60 font-medium">{block.name || block.phase}</p>
                    <p className="text-[10px] text-white/25 mt-0.5">{block.weeks}u | {block.weekly_km_range}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dates */}
          <div className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-5">
            <p className="uppercase tracking-[0.18em] text-[11px] font-medium text-white/40 mb-4">DATOER</p>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-3.5 h-3.5 text-white/25" />
                <div>
                  <p className="text-[10px] text-white/25 uppercase tracking-wider">Registrert</p>
                  <p className="text-sm text-white/60">{user.created_at ? new Date(user.created_at).toLocaleDateString('nb-NO', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
